/**
 * Merlin Extensions - Sentry Error Tracking
 * 
 * Provides unified Sentry integration for all extensions.
 * Only initializes in production environment.
 * 
 * Usage:
 * ```javascript
 * import { initSentry, captureError, setUser, addBreadcrumb } from '@merlin/lib/js/sentry';
 * 
 * // Initialize at app start
 * initSentry({
 *   dsn: 'https://xxx@sentry.io/xxx',
 *   extension: 'graph', // Extension name for tagging
 *   version: '1.0.0',
 *   environment: 'production', // Optional, defaults to checking process.env
 * });
 * 
 * // Capture errors with context
 * captureError(error, { extra: { userId: '123' } });
 * 
 * // Set user context
 * setUser({ id: '123', email: 'user@example.com' });
 * 
 * // Add breadcrumbs for debugging
 * addBreadcrumb({ category: 'ui', message: 'Button clicked' });
 * ```
 */

// Sentry CDN URL for browser SDK
const SENTRY_CDN = 'https://browser.sentry-cdn.com/8.45.0/bundle.tracing.min.js';

// Sentry state
let sentryState = {
    initialized: false,
    enabled: false,
    dsn: null,
    extension: null,
    Sentry: null,
};

let initPromise = Promise.resolve(false);

/**
 * Detect if we're in a production environment
 * @returns {boolean} True if production
 */
function isProduction() {
    // Check multiple sources for environment detection
    
    // 1. Check process.env.NODE_ENV (set by build tools)
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
        return true;
    }
    
    // 2. Check window.MERLIN_ENV (can be set by server-rendered pages)
    if (typeof window !== 'undefined' && window.MERLIN_ENV === 'production') {
        return true;
    }
    
    // 3. Check hostname (production domains)
    if (typeof window !== 'undefined' && window.location) {
        const hostname = window.location.hostname;
        // Consider production if not localhost/dev domains
        if (!hostname.includes('localhost') && 
            !hostname.includes('127.0.0.1') &&
            !hostname.includes('.local') &&
            !hostname.includes('dev.') &&
            !hostname.includes('staging.')) {
            return true;
        }
    }
    
    // 4. Check VITE_MODE (Vite build mode)
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'production') {
        return true;
    }
    
    return false;
}

function buildAllowUrlPatterns(extra = []) {
    const patterns = [
        /faxiom\.(ai|io)/,
        /merlin\./,
        /localhost/,
        ...extra,
    ];

    if (typeof window !== 'undefined' && window.location?.hostname) {
        const host = window.location.hostname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        patterns.push(new RegExp(host));
    }

    return patterns;
}

function buildIntegrations(Sentry) {
    const integrations = [];
    try {
        if (typeof Sentry.browserTracingIntegration === 'function') {
            integrations.push(Sentry.browserTracingIntegration());
        } else if (typeof Sentry.BrowserTracing === 'function') {
            integrations.push(new Sentry.BrowserTracing());
        }
    } catch (error) {
        console.warn('[Sentry] browser tracing integration unavailable:', error);
    }
    return integrations;
}

/**
 * Load Sentry SDK from CDN
 * @returns {Promise<object>} Sentry SDK
 */
async function loadSentrySdk() {
    // Already loaded
    if (typeof window !== 'undefined' && window.Sentry) {
        return window.Sentry;
    }
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SENTRY_CDN;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
            if (window.Sentry) {
                resolve(window.Sentry);
            } else {
                reject(new Error('Sentry SDK failed to load'));
            }
        };
        script.onerror = () => reject(new Error('Failed to load Sentry SDK'));
        document.head.appendChild(script);
    });
}

/**
 * Initialize Sentry error tracking
 * 
 * @param {object} options Configuration options
 * @param {string} options.dsn Sentry DSN (Data Source Name)
 * @param {string} options.extension Extension name (graph, chat, assistant, book)
 * @param {string} [options.version] Extension version
 * @param {string} [options.environment] Environment override (auto-detected if not provided)
 * @param {boolean} [options.debug=false] Enable debug mode
 * @param {number} [options.tracesSampleRate=0.1] Sample rate for performance traces (0-1)
 * @param {number} [options.replaysSessionSampleRate=0] Sample rate for session replay (0-1)
 * @param {number} [options.replaysOnErrorSampleRate=1.0] Sample rate for error replay (0-1)
 * @param {string[]} [options.allowUrls] URLs to capture errors from
 * @param {string[]} [options.denyUrls] URLs to ignore errors from
 * @returns {Promise<boolean>} True if initialized successfully
 */
async function initSentry(options = {}) {
    const {
        dsn,
        extension,
        version = '1.0.0',
        environment,
        debug = false,
        tracesSampleRate = 0.1,
        replaysSessionSampleRate = 0,
        replaysOnErrorSampleRate = 1.0,
        allowUrls = [],
        denyUrls = [],
    } = options;

    if (sentryState.initialized) {
        return initPromise;
    }

    sentryState.initialized = true;

    const isProd = environment === 'production' || isProduction();
    if (!isProd) {
        console.log('[Sentry] Skipping initialization - not in production environment');
        initPromise = Promise.resolve(false);
        return initPromise;
    }

    if (!dsn) {
        console.warn('[Sentry] No DSN provided, error tracking disabled');
        initPromise = Promise.resolve(false);
        return initPromise;
    }

    initPromise = (async () => {
        try {
            const Sentry = await loadSentrySdk();
            sentryState.Sentry = Sentry;

            Sentry.init({
                dsn,
                environment: environment || (typeof window !== 'undefined' && window.MERLIN_ENV) || 'production',
                release: `merlin-${extension}@${version}`,
                debug,

                tracesSampleRate,
                replaysSessionSampleRate,
                replaysOnErrorSampleRate,

                allowUrls: buildAllowUrlPatterns(allowUrls),

                denyUrls: [
                    /extensions\//i,
                    /^chrome:\/\//i,
                    /^chrome-extension:\/\//i,
                    /^moz-extension:\/\//i,
                    ...denyUrls,
                ],

                beforeSend(event) {
                    if (event.request?.headers) {
                        const headers = event.request.headers;
                        if (headers.Authorization) {
                            headers.Authorization = '[FILTERED]';
                        }
                        if (headers['X-API-Key']) {
                            headers['X-API-Key'] = '[FILTERED]';
                        }
                    }

                    event.tags = {
                        ...event.tags,
                        extension,
                        'merlin.version': version,
                    };

                    return event;
                },

                initialScope: {
                    tags: {
                        extension,
                        'merlin.version': version,
                    },
                },

                integrations: buildIntegrations(Sentry),
            });

            sentryState.enabled = true;
            sentryState.dsn = dsn;
            sentryState.extension = extension;

            if (typeof window !== 'undefined') {
                window.addEventListener('beforeunload', () => {
                    flush(500);
                });
            }

            console.log(`[Sentry] Initialized for ${extension}@${version} in ${environment || 'production'}`);
            return true;
        } catch (error) {
            console.error('[Sentry] Failed to initialize:', error);
            return false;
        }
    })();

    return initPromise;
}

/**
 * Capture an error with optional context
 * 
 * @param {Error|string} error Error object or message
 * @param {object} [context] Additional context
 * @param {object} [context.extra] Extra data to attach
 * @param {object} [context.tags] Tags for filtering
 * @param {string} [context.level] Error level (error, warning, info)
 * @param {object} [context.user] User context
 * @returns {string|null} Sentry event ID or null
 */
function captureError(error, context = {}) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        console.error('[Sentry] Not enabled, logging error:', error);
        return null;
    }
    
    const { extra, tags, level = 'error', user } = context;
    
    return sentryState.Sentry.withScope((scope) => {
        // Set level
        scope.setLevel(level);
        
        // Add extra context
        if (extra) {
            Object.entries(extra).forEach(([key, value]) => {
                scope.setExtra(key, value);
            });
        }
        
        // Add tags
        if (tags) {
            Object.entries(tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        
        // Set user
        if (user) {
            scope.setUser(user);
        }
        
        // Capture based on error type
        if (error instanceof Error) {
            return sentryState.Sentry.captureException(error);
        } else {
            return sentryState.Sentry.captureMessage(String(error), level);
        }
    });
}

/**
 * Capture a message (non-error)
 * 
 * @param {string} message Message to capture
 * @param {string} [level='info'] Log level (info, warning, error)
 * @param {object} [context] Additional context
 * @returns {string|null} Sentry event ID or null
 */
function captureMessage(message, level = 'info', context = {}) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        console.log(`[Sentry] Not enabled, logging message:`, message);
        return null;
    }
    
    return sentryState.Sentry.withScope((scope) => {
        scope.setLevel(level);
        
        if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => {
                scope.setExtra(key, value);
            });
        }
        
        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        
        return sentryState.Sentry.captureMessage(message, level);
    });
}

/**
 * Set user context for error tracking
 * 
 * @param {object|null} user User information (null to clear)
 * @param {string} [user.id] User ID
 * @param {string} [user.email] User email
 * @param {string} [user.username] Username
 */
function setUser(user) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return;
    }
    
    sentryState.Sentry.setUser(user);
}

/**
 * Add a breadcrumb for debugging
 * 
 * @param {object} breadcrumb Breadcrumb data
 * @param {string} breadcrumb.category Category (ui, http, navigation, etc.)
 * @param {string} breadcrumb.message Human-readable message
 * @param {string} [breadcrumb.level='info'] Level (info, warning, error)
 * @param {object} [breadcrumb.data] Additional data
 */
function addBreadcrumb(breadcrumb) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return;
    }
    
    sentryState.Sentry.addBreadcrumb({
        timestamp: Date.now() / 1000,
        level: 'info',
        ...breadcrumb,
    });
}

/**
 * Set a tag on the current scope
 * 
 * @param {string} key Tag key
 * @param {string} value Tag value
 */
function setTag(key, value) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return;
    }
    
    sentryState.Sentry.setTag(key, value);
}

/**
 * Set extra context data
 * 
 * @param {string} key Context key
 * @param {any} value Context value
 */
function setExtra(key, value) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return;
    }
    
    sentryState.Sentry.setExtra(key, value);
}

/**
 * Set the current transaction name (for performance tracing)
 * 
 * @param {string} name Transaction name
 */
function setTransactionName(name) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return;
    }
    
    const transaction = sentryState.Sentry.getCurrentScope()?.getTransaction();
    if (transaction) {
        transaction.setName(name);
    }
}

/**
 * Start a performance span
 * 
 * @param {object} options Span options
 * @param {string} options.name Span name
 * @param {string} [options.op] Operation type
 * @param {object} [options.data] Span data
 * @returns {object|null} Span object or null
 */
function startSpan(options) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return null;
    }
    
    return sentryState.Sentry.startSpan(options, () => {});
}

/**
 * Wrap a function to automatically capture errors
 * 
 * @param {Function} fn Function to wrap
 * @param {object} [context] Error context
 * @returns {Function} Wrapped function
 */
function wrapFunction(fn, context = {}) {
    return function wrapped(...args) {
        try {
            const result = fn.apply(this, args);
            
            // Handle async functions
            if (result && typeof result.catch === 'function') {
                return result.catch((error) => {
                    captureError(error, context);
                    throw error;
                });
            }
            
            return result;
        } catch (error) {
            captureError(error, context);
            throw error;
        }
    };
}

/**
 * Create an error boundary for async operations
 * 
 * @param {Promise} promise Promise to wrap
 * @param {object} [context] Error context
 * @returns {Promise} Wrapped promise
 */
async function withErrorBoundary(promise, context = {}) {
    try {
        return await promise;
    } catch (error) {
        captureError(error, context);
        throw error;
    }
}

/**
 * Check if Sentry is enabled
 * @returns {boolean} True if Sentry is enabled
 */
function isEnabled() {
    return sentryState.enabled;
}

function whenReady() {
    return initPromise;
}

/**
 * Flush pending events (useful before page unload)
 * @param {number} [timeout=2000] Timeout in milliseconds
 * @returns {Promise<boolean>} True if flushed successfully
 */
async function flush(timeout = 2000) {
    if (!sentryState.enabled || !sentryState.Sentry) {
        return true;
    }
    
    return sentryState.Sentry.flush(timeout);
}

/**
 * Setup global error handlers for uncaught errors and promise rejections
 * This is called automatically when Sentry is initialized
 */
function setupGlobalErrorHandlers() {
    if (typeof window === 'undefined') return;
    
    // Handle uncaught errors
    window.addEventListener('error', (event) => {
        if (!sentryState.enabled) return;
        
        captureError(event.error || event.message, {
            tags: { type: 'uncaught' },
            extra: {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            }
        });
    });
    
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        if (!sentryState.enabled) return;
        
        const error = event.reason instanceof Error 
            ? event.reason 
            : new Error(String(event.reason));
            
        captureError(error, {
            tags: { type: 'unhandledrejection' },
            extra: {
                reason: String(event.reason),
            }
        });
    });
    
    // Flush events before page unload
    window.addEventListener('beforeunload', () => {
        flush(500);
    });
    
    console.log('[Sentry] Global error handlers installed');
}

// Export for ES modules
export {
    initSentry,
    captureError,
    captureMessage,
    setUser,
    addBreadcrumb,
    setTag,
    setExtra,
    setTransactionName,
    startSpan,
    wrapFunction,
    withErrorBoundary,
    isEnabled,
    isProduction,
    whenReady,
    flush,
    setupGlobalErrorHandlers,
};

// Export for browser globals
if (typeof window !== 'undefined') {
    window.MerlinSentry = {
        initSentry,
        captureError,
        captureMessage,
        setUser,
        addBreadcrumb,
        setTag,
        setExtra,
        setTransactionName,
        startSpan,
        wrapFunction,
        withErrorBoundary,
        isEnabled,
        isProduction,
        whenReady,
        flush,
        setupGlobalErrorHandlers,
    };
}
